import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    Inject,
    Injectable,
    OnDestroy,
    TemplateRef,
    ViewEncapsulation,
    DOCUMENT
} from '@angular/core';
import { KAL_DIALOG_DATA, KalDialogConfig, KalDialogRef, KalDialogService } from '@kalidea/kaligraphi';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { KalTableComponent } from 'src/app/_shared/modules/kal-table/kal-table.component';

export interface FullScreenDialogData {
    selector: string;
    tableRef?: KalTableComponent<any>; // Optionel dans les autres contextes que celui du kal-table
    footer?: TemplateRef<any>;
}

export interface FullscreenOpenConfig extends FullScreenDialogData {
    config?: Omit<KalDialogConfig, 'data'>;
}

@Component({
    selector: 'app-fullscreen-dialog',
    template: `
        <div kalDialogContent style="padding: 20px">
            <div [id]="id"></div>
        </div>
        @if(data.footer) {
            <div kalDialogFooter>
                <ng-container *ngTemplateOutlet="data.footer"></ng-container>
            </div>
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FullScreenDialogComponent implements AfterViewInit, OnDestroy {

    readonly id = 'fullscreencontainer';

    constructor(private readonly dialogRef: KalDialogRef<FullScreenDialogComponent>,
                @Inject(KAL_DIALOG_DATA) public readonly data: FullScreenDialogData,
                @Inject(DOCUMENT) private readonly document: Document) {
    }

    /**
     * add class on dialog when footer is present
     */
    @HostBinding('class.has-footer')
    get hasFooter(): boolean {
        return !!this.data.footer;
    }

    private get selectorNode(): Element | null {
        return this.document.querySelector(this.data.selector);
    }

    ngAfterViewInit(): void {
        if (this.data.tableRef) {
            this.data.tableRef.isFullScreenContext = true;
        }
        this.document.getElementById(this.id).appendChild(this.selectorNode);
    }

    ngOnDestroy(): void {
        if (this.data.tableRef) {
            this.data.tableRef.isFullScreenContext = false;

            // Dans le cas d'un tableau (virtualisé) sorti du plein écran,
            // ce dernier est défiguré si ses colonnes ont été redimensionnées manuellement pendant le plein écran.
            // un nouveau rendu corrige le pb
            if(!!this.data.tableRef.virtual && this.selectorNode) {
                this.data.tableRef.render({recalculateColumnWidths: true});
            }
        }
    }

}

@Injectable({
    providedIn: 'root'
})
export class FullscreenService {

    private static anchorName = 'fullscreenanchor';

    private currentOpenedNodeSelector: string;

    private subscription = Subscription.EMPTY;

    private dialogRef: KalDialogRef<FullScreenDialogComponent, FullScreenDialogData>;

    private isFullscreenSubject$ = new BehaviorSubject<boolean>(false);

    isFullscreen$: Observable<boolean> = this.isFullscreenSubject$.asObservable();

    constructor(private readonly dialogService: KalDialogService,
                @Inject(DOCUMENT) private readonly document: Document) {
    }

    get hasAlreadyNodeInFullScreen(): boolean {
        return !!this.currentOpenedNodeSelector;
    }

    /**
     * retrieve first element mathing selector and move it to full screen dialog
     * @param selector CSS selector ( like `#myTable` or `.myTable` )
     * @param config override for dialog config
     * @param footer templateRef for dialog footer
     * @param tableRef kal-table component instance to perform update on resized column after closing
     */
    open({selector, config, footer, tableRef}: FullscreenOpenConfig): void {
        if (this.hasAlreadyNodeInFullScreen) {
            this.restoreContent();
        }

        this.isFullscreenSubject$.next(true);

        this.currentOpenedNodeSelector = selector;
        // get element to move
        const nodeToMove = this.document.querySelector(selector);

        // create anchor to locate where to reinsert node
        const anchor = this.document.createElement('div');
        anchor.setAttribute('id', FullscreenService.anchorName);
        nodeToMove.insertAdjacentElement('beforebegin', anchor);

        // open modal and inject node
        this.dialogRef = this.dialogService.open<FullScreenDialogComponent, FullScreenDialogData>(FullScreenDialogComponent, {
            hasBackdrop: true,
            width: '100vw',
            height: '100vh',
            title: 'Vue plein écran',
            data: {
                selector,
                footer,
                tableRef
            },
            backdropClass: 'cdk-overlay-dark-backdrop',
            ...config
        });

        this.subscription = this.dialogRef.afterClosed.pipe(
            tap(() => this.restoreContent())
        ).subscribe();
    }

    close(): void {
        this.dialogRef?.close();
    }

    private restoreContent(): void {
        this.subscription.unsubscribe();

        this.isFullscreenSubject$.next(false);

        // get anchor
        const anchor = this.document.getElementById(FullscreenService.anchorName);

        // get openedNode
        const nodeToRestore = this.document.querySelector(this.currentOpenedNodeSelector);

        // insert node and remove anchor
        anchor.insertAdjacentElement('afterend', nodeToRestore);
        anchor.remove();

        // clean
        this.currentOpenedNodeSelector = null;
    }

}
